import { formatDate } from "@intelligems/ig-utils";

import type {
  BillingActiveEntityViewModel,
  BillingTrialOutcomeViewModel,
} from "../types";
import { formatModuleList } from "./formatting";

type RemovalGroup = BillingTrialOutcomeViewModel["removals"][number];

/**
 * Every state that means the module is on its way out, whatever booked it.
 *
 * A scheduled removal is not the only exit: a module on a subscription of its own
 * carries `cancelledAt` instead and never gets a `removalEffectiveAt`, so reading
 * "is it staying?" off that one field promises a module the merchant is losing.
 * `state` is the projection's single answer to that question, so ask it.
 */
const DEPARTING_STATES = new Set<BillingActiveEntityViewModel["state"]>([
  "ended",
  "scheduled_cancel",
  "scheduled_removal",
]);

const keptSentence = (moduleNames: readonly string[]): string =>
  `You'll keep ${formatModuleList(moduleNames)} after your trial.`;

const removalSentence = ({ atLabel, moduleNames }: RemovalGroup): string => {
  const isSingle = moduleNames.length === 1;

  return `${formatModuleList(moduleNames)} ${
    isSingle ? "stays" : "stay"
  } active until ${atLabel} and ${isSingle ? "is" : "are"} removed then.`;
};

/**
 * The kept/removed split a trialing merchant is owed once they have picked a module
 * set different from the one the trial started with.
 *
 * Each sentence quotes the removal's OWN boundary rather than the plan's trial end:
 * the two agree on a classic trialing subscription, but a removal is parked at the
 * subscription's period end, and those can diverge once a subscription is migrated to
 * Stripe flexible billing (ADR 0013). Quoting a date the removal will not actually
 * happen on is the one failure a merchant cannot recover from on their own.
 *
 * Removals are grouped by boundary because `scheduledModuleRemovals` is a list of
 * independent parked commands, each with its own `effectiveAt`. In practice they
 * collapse to one group — every self-serve removal is planned at `period_end` — but a
 * second group must read as a second sentence rather than silently pick a date.
 *
 * Input order is preserved: the catalog already sorted these into canonical module
 * order, and re-sorting here would make this a second ordering authority.
 *
 * A module that is leaving by some route other than a scheduled removal is named in
 * neither sentence — it is not kept, and there is no removal boundary to quote for
 * it. Saying nothing about it is a gap; listing it as kept would be a lie.
 */
export const projectTrialOutcome = (
  activeModules: readonly BillingActiveEntityViewModel[]
): BillingTrialOutcomeViewModel | null => {
  const keptModuleNames = activeModules
    .filter(({ state }) => !DEPARTING_STATES.has(state))
    .map(({ name }) => name);

  const namesByBoundary = new Map<number, string[]>();
  for (const { name, removalEffectiveAt } of activeModules) {
    if (removalEffectiveAt === undefined) {
      continue;
    }
    const names = namesByBoundary.get(removalEffectiveAt);
    if (names) {
      names.push(name);
    } else {
      namesByBoundary.set(removalEffectiveAt, [name]);
    }
  }

  if (namesByBoundary.size === 0) {
    return null;
  }

  const removals: RemovalGroup[] = [...namesByBoundary]
    .sort(([left], [right]) => left - right)
    .map(([at, moduleNames]) => ({ atLabel: formatDate(at), moduleNames }));

  // The kept sentence is dropped rather than replaced when nothing survives: whole-plan
  // cancellation owns that state and says so in its own chip. Unreachable today — Smart
  // Content is mandatory and cannot be deselected — but a silent wrong sentence is
  // worse than a missing one.
  return {
    keptModuleNames,
    removals,
    tooltip: [
      ...(keptModuleNames.length > 0 ? [keptSentence(keptModuleNames)] : []),
      ...removals.map(removalSentence),
    ].join(" "),
  };
};
